<template>
  <div
    :class="[
      'vgp_date_picker',
      customSelector ? 'date_range_modification' : '',
      isRange ? 'range_picker' : '',
      hasRangeDiff ? 'range_has_diff' : '',
    ]"
  >
    <date-picker
      ref="calendar"
      :key="componentValue"
      v-model="componentValue"
      mode="date"
      :model-modifiers="modifiers"
      :class="animationClass"
      :is-required="!allowEmpty"
      :attributes="calendarAttributes"
      :disabled-dates="disabledDates"
      :locale="calendarLocale"
      :max-date="maxDate"
      :columns="columns"
      :popover="{
        placement: 'top-end',
        showDelay: 0,
        hideDelay: 0,
        transition: 'custom-popover-transition',
      }"
      :min-date="minDate"
      @drag="onDragUpdate"
      @update:modelValue="onInput"
      @dayclick="onDayClick"
      @popoverDidShow="onShow"
      @popoverDidHide="onHide"
    >
      <template
        v-if="$slots.default"

        #default="{ inputValue, togglePopover, inputEvents }"
      >
        <slot
          v-if="$slots.default"
          :inputValue="inputValue"
          :togglePopover="togglePopover"
          :inputEvents="inputEvents"
        />
      </template>

      <template
        v-if="hasFooterSlot"
        #footer
      >
        <div :class="[$style.vgp_date_picker_footer, showTimePicker ? $style.vgp_date_picker_footer_hide_footer : '']">
          <div :class="$style.default_footer">
            <vgp-button
              v-if="currentTime && timePicker"
              v-show="!pickerVisible"
              type="text-blue"
              :small="true"
              :label="currentTime"
              :icon="{ name: 'time', type: 'regular', size: 20 }"
              icon-side="left"
              @onClick="selectTimePicker"
            />
            <vgp-button
              v-if="resetButton && selectedDate"
              type="text-blue"
              :small="true"
              :label="locales('custom_column_recet_button_text')"
              @onClick="resetDate"
            />
            <slot
              v-if="$slots.footer"
              name="footer"
              :setValue="setValue"
            />
            <vgp-button
              v-else
              style="margin-left: auto"
              type="text-blue"
              :small="true"
              :label="locales('datepiker_today')"
              @onClick="setValue(new Date())"
            />
          </div>
          <div
            v-if="timePicker"
            :class="[animationClassForPicker, $style.timePicker_container]"
          >
            <vgp-time-picker
              v-if="timePicker"
              :current-date="value"
              :time-data="availableHours"
              :time-format="timeFormat"
              :back-button="true"
              :date-type="startOrEnd"
              :visible="pickerVisible"
              @changeTime="handleChangeTime"
              @backToDate="backClicked"
            />
          </div>
        </div>
      </template>
    </date-picker>
  </div>
</template>

<script>
import { DatePicker } from 'v-calendar';
import vgpTimePicker from './vgpTimePicker.vue';
import vgpButton from '$$uiKitComp/globalButton/vgpButton.vue';

export default {
  name: 'VgpDatePicker',
  components: {
    DatePicker, vgpTimePicker, vgpButton,
  },
  props: {
    value: { type: [Date, String, Number], default: null },
    workDays: { type: Array, required: false, default: () => [1, 2, 3, 4, 5] },
    rangeValue: {
      type: Object,
      required: false,
      default: () => ({
        start: null,
        end: null,
      }),
    },
    minDate: { type: Object, required: false, default: undefined },
    maxDate: { type: Object, required: false, default: undefined },
    isRange: { type: Boolean, required: false, default: false },
    columns: { type: Number, required: false, default: 1 },
    hasFooterSlot: { type: Boolean, required: false, default: true },
    backButton: {
      type: Boolean,
      default: false,
      required: false,
    },
    resetButton: {
      type: Boolean,
      default: false,
    },
    timeData: {
      type: Object,
      default: () => {},
      required: false,
    },
    timePicker: {
      type: Boolean,
      default: false,
      required: false,
    },
    stepMinutes: {
      type: Number,
      default: 5,
      required: false,
    },
    ganttId: {
      type: String,
      default: '',
      required: false,
    },
    startOrEnd: {
      type: String,
      default: '',
      required: false,
    },
    isCustomDateColumn: {
      type: Boolean,
      default: false,
      required: false,
    },
    selectedDate: {
      type: Boolean,
      default: false,
      required: false,
    },
    isRangeComponent: { type: Boolean, required: false, default: false },
    customSelector: { type: Boolean, required: false, default: false },
    calendarConfig: {
      type: Object,
      default: null,
    },
  },
  emits: {
    onChange: null,
    onShow: null,
    onClose: null,
    onChangeTime: null,
  },
  data() {
    const calendarWorkDays = this.workDays.map(day => (day === 7 ? 1 : ++day)); // Day of week begins on Sunday in v-calendar

    return {
      locales: __,
      calendarLocale: {
        id: this.getCalendarLocale(),
        firstDayOfWeek: +user.settings.start_monday ? 2 : 1,
        masks: {
          input: user.dateFormat ? user.dateFormat : 'YYYY-MM-DD',
        },
      },
      showTimePicker: false,
      pickerVisible: false,
      timeClicked: false,
      animationClass: '',
      animationClassForPicker: '',
      hasRangeDiff: false,
      anchorDate: null,
      calendarWorkDays,
      componentValue: this.isRange ? this.rangeValue : this.getClosestFutureWorkDay(this.value, calendarWorkDays),
    };
  },
  computed: {
    modifiers() {
      return {
        range: this.isRange,
      };
    },

    disabledDates() {
      if (this.isCustomDateColumn) {
        return undefined;
      }

      return [{
        start: null,
        end: null,
        repeat: {
          on: (day) => this.isBlockedDate(day),
        },
      }];
    },

    calendarAttributes() {
      const attrs = [
        {
          key: 'nonWorkDay',
          content: {
            style: { color: '#B2B2B2', pointerEvents: 'none' },
            class: 'dayHighlightActive',
          },
          dates: {
            start: null,
            end: null,
            repeat: {
              on: (day) => this.isBlockedDate(day),
            },
          },
        },
      ];

      if (this.anchorDate) {
        attrs.push({
          key: 'rangeAnchor',
          highlight: { fillMode: 'solid', color: 'blue' },
          content: { class: 'vgp-range-anchor' },
          dates: this.anchorDate,
        });
      }

      return attrs;
    },

    timeFormat() {
      return user.timeFormat;
    },

    availableHours() {
      return this.calendarConfig ? this.calendarConfig.parsed.hours : {};
    },

    allowEmpty() {
      return this.resetButton && this.isCustomDateColumn;
    },

    currentTime() {
      if (this.timePicker && this.value) {
        let hours = this.value.getHours();
        const minutes = this.value.getMinutes().toString().padStart(2, '0');
        let ampm = 'AM';

        if (!this.timeFormat) {
          if (hours > 12) {
            hours -= 12;
            ampm = 'PM';
          }

          return `${hours < 10 ? `0${hours}` : hours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
        }

        return `${hours < 10 ? `0${hours}` : hours}:${minutes}`;
      }

      return null;
    },
  },
  watch: {
    componentValue(value) {
      if (this.isRange) {
        const startDate = new Date(value.start);
        const endDate = new Date(value.end);
        const isDifferentDay = startDate.getMonth() !== endDate.getMonth() || startDate.getDate() !== endDate.getDate();

        if (isDifferentDay) {
          this.hasRangeDiff = true;
        } else {
          this.hasRangeDiff = false;
        }
      }
    },
    rangeValue(val) {
      if (this.isRange) {
        this.componentValue = val;
      }
    },
    value(val) {
      if (!this.isRange) {
        this.componentValue = this.getClosestFutureWorkDay(val, this.calendarWorkDays);
      }
    },
  },
  methods: {
    setValue(value) {
      const closestWorkDay = this.getClosestFutureWorkDay(value, this.calendarWorkDays);
      const isSameDate = this.isSameDate(this.componentValue, closestWorkDay);

      this.$refs.calendar?.updateValue(closestWorkDay);
      this.$refs.calendar?.hidePopover();

      if (isSameDate) {
        this.$emit('onChange', closestWorkDay, this.startOrEnd);
      }
    },

    onDragUpdate(value) {
      if (!this.isRange) return;
      const start = value?.start ? new Date(value.start) : null;
      const end = value?.end ? new Date(value.end) : null;
      if (!start || !end) return;

      if (start.toDateString() === end.toDateString()) {
        this.anchorDate = start;
        this.hasRangeDiff = false;
      } else {
        this.hasRangeDiff = true;
      }
    },

    onInput(value) {
      if (this.isRange) {
        this.anchorDate = null;

        if (value?.start && value?.end) {
          const start = new Date(value.start);
          const end = new Date(value.end);
          this.hasRangeDiff = start.toDateString() !== end.toDateString();
        }
      }

      this.$emit('onChange', value, this.startOrEnd);
    },

    onDayClick(day) {
      if (this.isRange || day?.isDisabled) {
        return;
      }

      const date = this.isCustomDateColumn
        ? day.date
        : this.getClosestFutureWorkDay(day.date, this.calendarWorkDays);

      this.$emit('onChange', date, this.startOrEnd);
    },

    isSameDate(first, second) {
      if (!first && !second) {
        return true;
      }

      if (!first || !second) {
        return false;
      }

      return new Date(first).toDateString() === new Date(second).toDateString();
    },

    onShow() {
      this.$emit('onShow');
    },

    onHide() {
      this.$emit('onClose');
    },

    getClosestFutureWorkDay(date, workDays = []) {
      if (!date) {
        return null;
      }

      if (!workDays.length) {
        return date;
      }
      if (this.isCustomDateColumn) return date;

      let closestDay = new Date();

      if (date && date !== '') {
        closestDay = new Date(date);
      }

      let i = 0;

      while (!workDays.includes(closestDay.getDay() + 1) && i < 7) {
        closestDay.setDate(closestDay.getDate() + 1);
        i++;
      }

      return closestDay;
    },
    handleChangeTime(value) {
      this.$emit('onChangeTime', value, this.startOrEnd);
    },
    selectTimePicker() {
      this.animationClass = 'hide-date';
      this.animationClassForPicker = 'show-picker';
      this.pickerVisible = true;
      this.timeClicked = false;
      this.showTimePicker = false;
    },
    backClicked() {
      this.animationClass = 'show-date';
      this.animationClassForPicker = 'hide-picker';
      this.pickerVisible = false;
      this.timeClicked = false;
      this.showTimePicker = false;

      setTimeout(() => {
        this.animationClass = '';
        this.animationClassForPicker = '';
      }, 500);
    },
    move(date) {
      this.$refs.calendar.move(date);
    },
    resetDate() {
      this.componentValue = null;
      this.$refs.calendar?.updateValue(null);
      this.$emit('onChange', null);
    },
    getCalendarLocale() {
      if (user.locale === 'kr') {
        return 'ko';
      }

      return user.locale;
    },
    isBlockedDate(days) {
      if (this.isCustomDateColumn) return;
      if (!this.calendarConfig) {
        return !this.calendarWorkDays.find(workDay => workDay === days.weekday);
      }

      return !this._isWorkDay(days.date, this.calendarConfig);
    },

    _isWorkDay(date, calendarConfig) {
      if (!calendarConfig) return true;

      const d = new Date(date);
      const dayUtc = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
      const { dates } = calendarConfig;
      const weekday = d.getDay();

      const overrideKey = Object.keys(dates).find(k => k.length >= 2 && +k === dayUtc);

      return overrideKey !== undefined ? !!dates[overrideKey] : !!dates[weekday];
    },
  },
};
</script>

<style lang="less" src="./datePicker.less" ></style>
<style module lang="less" src="./vgpDatePicker.less"></style>
